import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatGameTime } from "../../format/formatGameTime";
import type { MatchSummary } from "../../model/Analysis";
import { AssetIcon } from "../AssetIcon";
import { ScoreRing } from "../ScoreRing";

const WIN = msg({ id: "analysis.match.win", message: "Victory" });
const LOSS = msg({ id: "analysis.match.loss", message: "Defeat" });
const MATCH_LINE = msg({
  id: "analysis.match.line",
  message: "Match {matchId} · {duration} · {startedAt}",
});
const KDA = msg({ id: "analysis.match.kda", message: "K/D/A {kills} / {deaths} / {assists}" });
const SOULS = msg({
  id: "analysis.match.souls",
  message: "{souls, plural, one {# soul} other {# souls}}",
});
// Без «50 — типичный»: рядом с кольцом «48» это читалось как оценка 50.
const SCORE_CAPTION = msg({
  id: "analysis.match.score_better",
  message: "Better than {score} of 100 {hero} players of your rank",
});

type MatchHeaderProps = {
  match: MatchSummary;
  // Строка внизу шапки во всю ширину: с кем сравнивает разбор (`BaselineLine`).
  children?: ReactNode;
};

// Шапка разбора по макету: герой, исход, номер, длительность и дата матча, K/D/A и души,
// быстрая оценка матча в кольце (`MatchSummary.score`, её может ещё не быть) и строка
// под ними, если её передали.
export const MatchHeader = ({ match, children }: MatchHeaderProps) => {
  const { _, i18n } = useLingui();
  const hero = useAssetLookup("heroes")(match.hero_id);
  const startedAt = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(match.started_at));
  return (
    <header className="flex flex-wrap items-center gap-x-6 gap-y-4 rounded-xl border border-border bg-surface px-5 py-4.5">
      <AssetIcon asset={hero} size={64} className="rounded-[10px]" />
      <div className="flex min-w-0 grow flex-col gap-0.5">
        <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1.5">
          <h1 className="font-display text-[34px] leading-[1.1] font-bold">{hero.name}</h1>
          <span
            data-result={match.won ? "win" : "loss"}
            className={cn(
              "rounded px-2.5 py-0.5 text-[13px] font-semibold",
              match.won ? "bg-good/15 text-good" : "bg-bad/15 text-bad",
            )}
          >
            {_(match.won ? WIN : LOSS)}
          </span>
          <span className="text-[13px] text-subtle">
            {_({
              ...MATCH_LINE,
              values: {
                matchId: String(match.match_id),
                duration: formatGameTime(match.duration_s),
                startedAt,
              },
            })}
          </span>
        </div>
        <p className="font-mono text-[15px] text-foreground-soft">
          {_({
            ...KDA,
            values: { kills: match.kills, deaths: match.deaths, assists: match.assists },
          })}
          {" · "}
          {_({ ...SOULS, values: { souls: match.net_worth } })}
        </p>
      </div>
      {match.score !== null && (
        <div className="flex items-center gap-3.5">
          <ScoreRing score={match.score} size="lg" />
          <p className="max-w-[200px] text-[13px] text-muted">
            {_({ ...SCORE_CAPTION, values: { hero: hero.name, score: match.score } })}
          </p>
        </div>
      )}
      {children}
    </header>
  );
};
